import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from '@aimsos/i18n';

import { listMembers, validateApiResponse } from '../../../services';
import { GROUP_MEMBERS_PAGE_SIZE } from '../constants/groups';
import { MEMBER_STATUS } from '../constants/copy';
import { mapApiGroupMemberToUi } from '../utils/groupMappers';

const NONE = Object.freeze([]);

/**
 * People that "Add members" can offer: workspace members that are not in the group yet (a removed
 * member is not offered). `enabled` defers the load until the modal opens.
 *
 * @param {string} groupId
 * @param {{ enabled?: boolean }} [options]
 */
export const useMemberCandidates = (groupId, { enabled = true } = {}) => {
  const { t } = useTranslation('people-access');
  const [search, setSearch] = useState('');
  const [settled, setSettled] = useState({ key: null, session: 0, candidates: [], error: null });
  const fetchIdRef = useRef(0);
  // Every opening is a new session: the list of the last one (people who were just added) must
  // not be offered again while the new one loads.
  const [session, setSession] = useState(0);
  const [wasEnabled, setWasEnabled] = useState(enabled);
  if (enabled !== wasEnabled) {
    setWasEnabled(enabled);
    if (enabled) setSession((n) => n + 1);
  }
  const requestKey = `${groupId}|${search}|${session}`;

  useEffect(() => {
    if (!enabled) return undefined;
    const fetchId = ++fetchIdRef.current;
    let mounted = true;
    const isCurrent = () => mounted && fetchId === fetchIdRef.current;

    listMembers({ page_size: GROUP_MEMBERS_PAGE_SIZE, search: { query: search } })
      .then((res) => validateApiResponse(res, t('groups.addMembers.loadError')))
      .then(({ body }) => ({
        key: requestKey,
        session,
        candidates: (body?.items ?? [])
          .filter((m) => !m.group_ids.includes(groupId))
          .map(mapApiGroupMemberToUi)
          .filter((m) => m.status !== MEMBER_STATUS.REMOVED),
        error: null,
      }))
      .catch((err) => ({
        key: requestKey,
        session,
        candidates: [],
        error: err?.message ?? t('groups.addMembers.loadError'),
      }))
      .then((next) => {
        if (isCurrent()) setSettled(next);
      });

    return () => {
      mounted = false;
    };
  }, [groupId, enabled, search, session, requestKey, t]);

  const sameSession = settled.session === session;
  const candidates = sameSession ? settled.candidates : NONE;
  const error = sameSession ? settled.error : null;
  const isLoading = enabled && settled.key !== requestKey;

  return useMemo(
    () => ({ candidates, isLoading, error, search, setSearch }),
    [candidates, isLoading, error, search],
  );
};
